import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Select,
    Table,
    Alert,
    Form,
    Space,
    Button,
    Typography,
    Divider,
    Tag,
    Row,
    Col,
    Statistic,
    Switch,
    message,
    Empty,
    Input
} from 'antd';
import {
    TableOutlined,
    FileExcelOutlined,
    DatabaseOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    DownloadOutlined,
    FilterOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Title, Text } = Typography;

const TableNodeProperties = ({ data = {}, updateNodeData, nodes = [], edges = [] }) => {
    // Use data as the source of truth instead of local state
    const [previewData, setPreviewData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    // Get values directly from data prop (source of truth)
    const selectedSourceNode = data?.config?.sourceNodeId || null;
    const selectedSchema = data?.config?.selectedSchema || [];
    const selectedSheet = data?.config?.selectedSheet || null;

    // Debug logging
    useEffect(() => {
        console.log('TableNodeProperties data update:');
        console.log('- selectedSourceNode:', selectedSourceNode);
        console.log('- selectedSchema.length:', selectedSchema.length);
        console.log('- selectedSheet:', selectedSheet);
        console.log('- nodes:', nodes);
        console.log('- data:', data);
        console.log('- Node ID:', data?.id || 'unknown');
    }, [selectedSourceNode, selectedSchema, selectedSheet, nodes, data]);

    // Sync with data prop changes (when loading saved rule or switching between nodes)
    useEffect(() => {
        console.log('TableNodeProperties: Syncing with data prop changes for node:', data?.id);
        // Clear preview data when node data changes
        setPreviewData([]);
    }, [data?.id, data?.config?.sourceNodeId, data?.config?.selectedSheet, data?.config?.selectedSchema]);

    // Find all Excel/CSV input nodes that are upstream or connected
    const availableSourceNodes = useMemo(() => {
        console.log('Computing available source nodes...');
        console.log('All nodes:', nodes);
        
        // Filter nodes to only include Excel/CSV input nodes with data
        // Dữ liệu được lưu trong node.data.config (sau khi tối ưu storage)
        const sourceNodes = nodes.filter(node => {
            const nodeType = node.data?.nodeType || node.data?.type;
            const config = node.data?.config || {};
            // Kiểm tra cả config (mới) và data trực tiếp (backward compatibility)
            const hasData = config?.sheets || config?.fileName || node.data?.sheets || node.data?.fileName;
            
            console.log(`Node ${node.id}: type=${nodeType}, hasData=${!!hasData}, config:`, config);
            
            return (nodeType === 'excel_input' || nodeType === 'csv_input') && hasData;
        });

        console.log('Filtered source nodes:', sourceNodes);
        return sourceNodes;
    }, [nodes]);

    // Get schema and data from selected source node
    const sourceNodeData = useMemo(() => {
        if (!selectedSourceNode) return null;
        
        const sourceNode = availableSourceNodes.find(node => node.id === selectedSourceNode);
        if (!sourceNode) return null;

        console.log('Selected source node data:', sourceNode.data);
        
        // Ưu tiên đọc từ config (nơi dữ liệu được lưu sau tối ưu)
        const config = sourceNode.data?.config || {};
        
        // Lấy sheets từ config, nếu không có thì fallback về data trực tiếp (backward compatibility)
        let sheets = config?.sheets || sourceNode.data?.sheets || [];
        
        // Rebuild headers từ schema nếu cần (vì optimized storage không lưu headers riêng)
        sheets = sheets.map(sheet => ({
            ...sheet,
            headers: sheet.headers || (sheet.schema ? sheet.schema.map(col => col.columnName) : [])
        }));
        
        return {
            id: sourceNode.id,
            fileName: config?.fileName || sourceNode.data?.fileName,
            fileType: config?.fileType || sourceNode.data?.fileType,
            sheets: sheets,
            selectedSheet: config?.selectedSheet || sourceNode.data?.selectedSheet
        };
    }, [selectedSourceNode, availableSourceNodes]);

    // Get available sheets from source node
    const availableSheets = useMemo(() => {
        if (!sourceNodeData || !sourceNodeData.sheets) return [];
        return sourceNodeData.sheets.map(sheet => ({
            name: sheet.name,
            totalRows: sheet.totalRows,
            headers: sheet.headers,
            schema: sheet.schema
        }));
    }, [sourceNodeData]);

    // Get current sheet data
    const currentSheetData = useMemo(() => {
        if (!sourceNodeData || !selectedSheet) return null;
        
        const sheet = sourceNodeData.sheets.find(s => s.name === selectedSheet);
        console.log('Current sheet data:', sheet);
        
        return sheet || null;
    }, [sourceNodeData, selectedSheet]);

    // Safe update function that preserves node type
    const safeUpdateNodeData = (newConfig) => {
        if (typeof updateNodeData === 'function') {
            const updatedData = {
                nodeType: data?.nodeType || 'table',
                type: data?.type || 'table',
                // Don't override customName and label if they're provided in newConfig
                ...(newConfig.customName ? {} : { label: data?.label || 'Table' }),
                onNodeClick: data?.onNodeClick,
                config: {
                    ...data?.config,
                    ...newConfig
                },
                ...newConfig
            };
            updateNodeData(updatedData);
        }
    };

    // Handle source node selection
    const handleSourceNodeChange = (nodeId) => {
        console.log('Source node changed to:', nodeId);
        setPreviewData([]);
        
        // Update node data immediately
        safeUpdateNodeData({
            sourceNodeId: nodeId,
            selectedSchema: [],
            selectedSheet: null,
            tableName: null
        });
    };

    // Handle sheet selection
    const handleSheetChange = (sheetName) => {
        console.log('Sheet changed to:', sheetName);
        
        const sheet = availableSheets.find(s => s.name === sheetName);
        if (sheet) {
            const newSchema = sheet.schema || [];
            // For table node, we don't need preview data since we're just configuring schema
            // Preview will be generated based on schema during execution
            setPreviewData([]);
            
            // Generate meaningful node name from file and sheet
            const baseFileName = sourceNodeData?.fileName?.replace(/\.(xlsx|xls|csv)$/i, '') || 'data';
            const nodeName = sheetName === 'CSV Data' ? baseFileName : `${baseFileName}_${sheetName}`;
            
            console.log('Auto-updating node name to:', nodeName);
            
            // Update node data with both config and name in single call
            const updatedData = {
                nodeType: data?.nodeType || 'table',
                type: data?.type || 'table',
                customName: nodeName,
                label: nodeName,
                onNodeClick: data?.onNodeClick,
                config: {
                    ...data?.config,
                    sourceNodeId: selectedSourceNode,
                    selectedSheet: sheetName,
                    selectedSchema: newSchema,
                    headers: sheet.headers || [],
                    totalRows: sheet.totalRows || 0,
                    tableName: `${sourceNodeData?.fileName || 'data'}_${sheetName}`
                }
            };
            
            console.log('Auto-updating node name to:', nodeName);
            console.log('Full updated data:', updatedData);
            
            updateNodeData(updatedData);
            
            console.log('Node data updated successfully');
        }
    };

    // NEW: Auto-select source node based on connections
    useEffect(() => {
        // If source is already selected, verify it still exists
        if (selectedSourceNode) {
             const sourceExists = nodes.find(n => n.id === selectedSourceNode);
             if (!sourceExists) {
                 // Source was deleted, clear selection
                 console.log('Source node deleted, clearing selection');
                 safeUpdateNodeData({
                    sourceNodeId: null,
                    selectedSheet: null,
                    selectedSchema: [],
                    tableName: null
                 });
             }
             return; 
        }

        // Find upstream node connected to this node
        const incomingEdge = edges.find(edge => edge.target === data.id);
        if (incomingEdge) {
            const sourceNode = nodes.find(n => n.id === incomingEdge.source);
            // Check if it is a valid source type
            const nodeType = sourceNode?.data?.nodeType || sourceNode?.data?.type;
            if (sourceNode && (nodeType === 'excel_input' || nodeType === 'csv_input')) {
                console.log('Auto-selecting connected source node:', sourceNode.id);
                handleSourceNodeChange(sourceNode.id);
            }
        } else {
            // Fallback: If no connection, but there is exactly one Excel/CSV node in the graph
            const validSources = nodes.filter(n => {
                const type = n.data?.nodeType || n.data?.type;
                const config = n.data?.config || {};
                // Kiểm tra cả config (mới) và data trực tiếp (backward compatibility)
                return (type === 'excel_input' || type === 'csv_input') && 
                       (config?.fileName || config?.sheets || n.data?.fileName || n.data?.sheets);
            });
            
            if (validSources.length === 1) {
                 console.log('Auto-selecting single available source node:', validSources[0].id);
                 handleSourceNodeChange(validSources[0].id);
            }
        }
    }, [edges, nodes, data.id, selectedSourceNode]);

    // NEW: Validate selected sheet against available sheets (in case file changed)
    useEffect(() => {
        if (selectedSheet && availableSheets.length > 0) {
            const sheetExists = availableSheets.some(s => s.name === selectedSheet);
            if (!sheetExists) {
                console.log('Selected sheet no longer exists, resetting...');
                safeUpdateNodeData({
                    selectedSheet: null,
                    selectedSchema: [],
                    headers: [],
                    totalRows: 0,
                    tableName: null
                });
            }
        }
    }, [selectedSheet, availableSheets]);

    // Handle schema column selection/deselection
    const handleSchemaToggle = (columnIndex, enabled) => {
        const newSchema = [...selectedSchema];
        newSchema[columnIndex] = {
            ...newSchema[columnIndex],
            enabled: enabled
        };
        
        // Update node data immediately
        safeUpdateNodeData({
            sourceNodeId: selectedSourceNode,
            selectedSheet: selectedSheet,
            selectedSchema: newSchema
        });
    };

    // Generate preview data for table display - optimized for no stored preview data
    const generatePreviewData = () => {
        if (!currentSheetData || !selectedSchema.length) return [];
        
        const enabledColumns = selectedSchema.filter(col => col.enabled !== false);
        
        // Generate sample preview based on schema instead of stored preview data
        return Array(3).fill(null).map((_, rowIndex) => {
            const rowData = { key: rowIndex };
            enabledColumns.forEach(col => {
                // Generate sample data based on column type
                switch (col.dataType) {
                    case 'integer':
                        rowData[col.columnName] = `${Math.floor(Math.random() * 100)}`;
                        break;
                    case 'float':
                        rowData[col.columnName] = `${(Math.random() * 100).toFixed(2)}`;
                        break;
                    case 'boolean':
                        rowData[col.columnName] = Math.random() > 0.5 ? 'true' : 'false';
                        break;
                    case 'date':
                        rowData[col.columnName] = new Date().toISOString().split('T')[0];
                        break;
                    default:
                        rowData[col.columnName] = `Sample ${col.columnName} ${rowIndex + 1}`;
                }
            });
            return rowData;
        });
    };

    // Table columns for schema configuration
    const schemaColumns = [
        {
            title: 'Include',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 80,
            render: (enabled, record, index) => (
                <Switch
                    size="small"
                    checked={enabled !== false}
                    onChange={(checked) => handleSchemaToggle(index, checked)}
                />
            )
        },
        {
            title: 'Column Name',
            dataIndex: 'columnName',
            key: 'columnName',
            width: 150,
            render: (text, record) => (
                <Text strong style={{ 
                    color: record.enabled === false ? '#bfbfbf' : '#262626'
                }}>
                    {text}
                </Text>
            )
        },
        {
            title: 'Data Type',
            dataIndex: 'dataType',
            key: 'dataType',
            width: 100,
            render: (type, record) => {
                const colors = {
                    'string': 'blue',
                    'integer': 'green', 
                    'float': 'orange',
                    'boolean': 'purple',
                    'date': 'red'
                };
                return (
                    <Tag 
                        color={record.enabled === false ? 'default' : colors[type]} 
                        style={{ opacity: record.enabled === false ? 0.5 : 1 }}
                    >
                        {type}
                    </Tag>
                );
            }
        },
        {
            title: 'Sample Values',
            dataIndex: 'sampleValues',
            key: 'sampleValues',
            render: (values, record) => (
                <div style={{ opacity: record.enabled === false ? 0.5 : 1 }}>
                    {(values || []).slice(0, 2).map((val, idx) => (
                        <Tag key={idx} style={{ margin: '1px', fontSize: '11px' }}>
                            {String(val).length > 12 ? String(val).substring(0, 12) + '...' : val}
                        </Tag>
                    ))}
                </div>
            )
        }
    ];

    // Generate columns for preview table
    const previewColumns = useMemo(() => {
        const enabledColumns = selectedSchema.filter(col => col.enabled !== false);
        return enabledColumns.map(col => ({
            title: col.columnName,
            dataIndex: col.columnName,
            key: col.columnName,
            width: 150,
            ellipsis: true,
            render: (text) => (
                <span style={{ fontSize: '12px' }}>
                    {String(text || '').length > 20 ? 
                        String(text || '').substring(0, 20) + '...' : 
                        text || ''
                    }
                </span>
            )
        }));
    }, [selectedSchema]);

    // Apply schema selection (mark all columns as selected)
    const handleApplyAllColumns = () => {
        if (!currentSheetData) return;
        
        const allEnabledSchema = currentSheetData.schema.map(col => ({
            ...col,
            enabled: true
        }));
        
        safeUpdateNodeData({
            sourceNodeId: selectedSourceNode,
            selectedSheet: selectedSheet,
            selectedSchema: allEnabledSchema
        });
        
        message.success('All columns selected');
    };

    // Clear selection
    const handleClearSelection = () => {
        const clearedSchema = selectedSchema.map(col => ({
            ...col,
            enabled: false
        }));
        
        safeUpdateNodeData({
            sourceNodeId: selectedSourceNode,
            selectedSheet: selectedSheet,
            selectedSchema: clearedSchema
        });
        
        message.success('All columns deselected');
    };

    return (
        <div style={{ padding: '0' }}>
            <Title level={4}>
                <TableOutlined /> Table Configuration
            </Title>

            {/* Step 1: Select Source Node */}
            <Card size="small" title="1. Select Data Source" style={{ marginBottom: 16 }}>
                {availableSourceNodes.length === 0 ? (
                    <Empty
                        image={<FileExcelOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />}
                        description="No Excel/CSV input nodes found"
                    >
                        <Text type="secondary">
                            Please add and configure Excel Input or CSV Input nodes first
                        </Text>
                    </Empty>
                ) : (
                    <Form.Item label="Source Node" style={{ marginBottom: 8 }}>
                        <Select
                            placeholder="Select a data source node"
                            value={selectedSourceNode}
                            onChange={handleSourceNodeChange}
                            style={{ width: '100%' }}
                        >
                            {availableSourceNodes.map(node => (
                                <Option key={node.id} value={node.id}>
                                    <Space>
                                        <FileExcelOutlined />
                                        <span>{node.data?.config?.fileName || node.data?.fileName || node.id}</span>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            ({(node.data?.config?.fileType || node.data?.fileType || 'file')?.toUpperCase()})
                                        </Text>
                                    </Space>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
            </Card>

            {/* Step 2: Select Sheet (if Excel with multiple sheets) */}
            {sourceNodeData && availableSheets.length > 1 && (
                <Card size="small" title="2. Select Sheet" style={{ marginBottom: 16 }}>
                    <Form.Item label="Sheet" style={{ marginBottom: 8 }}>
                        <Select
                            placeholder="Select a sheet"
                            value={selectedSheet}
                            onChange={handleSheetChange}
                            style={{ width: '100%' }}
                        >
                            {availableSheets.map(sheet => (
                                <Option key={sheet.name} value={sheet.name}>
                                    <Space>
                                        <TableOutlined />
                                        <span>{sheet.name}</span>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            ({sheet.totalRows} rows, {sheet.headers?.length} cols)
                                        </Text>
                                    </Space>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                        💡 Node name will be automatically updated based on selected sheet
                    </Text>
                </Card>
            )}

            {/* Auto-select single sheet */}
            {sourceNodeData && availableSheets.length === 1 && !selectedSheet && (
                (() => {
                    // Auto-select the only available sheet and update node name
                    const sheet = availableSheets[0];
                    setTimeout(() => {
                        handleSheetChange(sheet.name);
                    }, 0);
                    return null;
                })()
            )}

            {/* Step 3: Configure Schema */}
            {currentSheetData && (
                <Card 
                    size="small" 
                    title={`3. Configure Schema (${currentSheetData.schema?.length || 0} columns)`}
                    extra={
                        <Space>
                            <Button size="small" onClick={handleApplyAllColumns}>
                                Select All
                            </Button>
                            <Button size="small" onClick={handleClearSelection}>
                                Clear All
                            </Button>
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        <Col span={8}>
                            <Statistic 
                                title="Total Columns" 
                                value={selectedSchema.length} 
                                prefix={<DatabaseOutlined />} 
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic 
                                title="Selected" 
                                value={selectedSchema.filter(col => col.enabled !== false).length}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic 
                                title="Total Rows" 
                                value={currentSheetData.totalRows || 0}
                                prefix={<TableOutlined />}
                            />
                        </Col>
                    </Row>

                    <Table
                        columns={schemaColumns}
                        dataSource={selectedSchema.map((col, index) => ({ ...col, key: index }))}
                        pagination={false}
                        size="small"
                        scroll={{ y: 200 }}
                        bordered
                    />
                </Card>
            )}

            {/* Step 4: Preview */}
            {currentSheetData && selectedSchema.some(col => col.enabled !== false) && (
                <Card 
                    size="small" 
                    title="4. Data Preview" 
                    extra={
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => setShowPreview(!showPreview)}
                        >
                            {showPreview ? 'Hide' : 'Show'} Preview
                        </Button>
                    }
                >
                    {showPreview && (
                        <Table
                            columns={previewColumns}
                            dataSource={generatePreviewData()}
                            pagination={false}
                            size="small"
                            scroll={{ x: previewColumns.length * 150, y: 200 }}
                            bordered
                            title={() => (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Sample preview data (schema-based). Actual data: {currentSheetData.totalRows} total rows
                                </Text>
                            )}
                        />
                    )}
                </Card>
            )}

            {/* Configuration Summary */}
            {selectedSourceNode && selectedSheet && (
                <Alert
                    type="success"
                    message="Table Configuration Complete"
                    description={
                        <div>
                            <Text strong>Source:</Text> {sourceNodeData?.fileName} → {selectedSheet} <br/>
                            <Text strong>Columns:</Text> {selectedSchema.filter(col => col.enabled !== false).length} selected <br/>
                            <Text strong>Table Name:</Text> {data?.config?.tableName || 'data_table'}
                        </div>
                    }
                    style={{ marginTop: 16 }}
                    action={
                        <Button size="small" icon={<DownloadOutlined />}>
                            Export Schema
                        </Button>
                    }
                />
            )}

            {/* No Configuration State */}
            {!selectedSourceNode && (
                <Card style={{ marginTop: 16 }}>
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <InfoCircleOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                        <div style={{ marginTop: 16 }}>
                            <Text>Configure this table node to extract data from Excel/CSV inputs</Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">
                                Select a data source, choose columns, and preview the table structure
                            </Text>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default TableNodeProperties;